<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import TableLiquidation from './TableLiquidation.vue'
import AdditionalConcepts from './AdditionalConcepts.vue'
import Popper from 'vue3-popper'
import { useI18n } from 'vue-i18n'
import {
  DToggleField,
  DTextAreaField,
  DTextField,
  DModal,
  DBtn,
  Trash03Icon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
  Edit01Icon,
  BarChart10Icon,
  CheckIcon,
  Loading02Icon,
  CalculatorIcon,
  PrinterIcon
} from 'front-library'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { formatDynamicNumber } from '@/app/core/util/numbers'
dayjs.extend(utc)

interface Adjust {
  conceptId: string
  quantity: any
  amount: any
  adjustAverage: any[]
}

interface AdjustsData {
  isRecontractable: any
  observation: any
  conceptsToAdd: any[]
  adjusts: Adjust[]
}

const props = defineProps<{
  employee: any
  liquidation: any
  index: number
  itemsAdditionalConcepts: any[]
  indexEmployee?: any
  showDropdown?: boolean
}>()

const emit = defineEmits([
  'check:liquidation',
  'remove:employee',
  'get:movements',
  'update:employee',
  'reprocess:employee',
  'liquidationReport:employee'
])

/* Datos */
const { t } = useI18n()
const isRecontractable = ref()
const observation = ref()
const adjustQuantity = ref()
const adjustAmount = ref()
const concepts = ref([])
const openAdditionalConcepts = ref(false)
const additionalConcepts = ref()
const conceptsEmployee = ref()
const payNeto = ref()

watch(
  [() => props.employee.observation, () => props.employee.isRecontractable],
  () => {
    observation.value = props.employee.observation
    isRecontractable.value = props.employee.isRecontractable
  }
)

watch([() => props.indexEmployee, () => props.employee.concepts], () => {
  showTable.value = true
  conceptsEmployee.value = props.employee.concepts.filter(
    (item: any) => item.conceptName && !item.conceptName.includes('999999')
  )
  payNeto.value = props.employee.concepts.filter(
    (item: any) => item.conceptName && item.conceptName.includes('999999')
  )[0]?.amount
})

watch(conceptsEmployee, (newValue, oldValue) => {
  if (newValue.length > 0) {
    let iconPlus = document.getElementById('plus-button-' + props.index)
    iconPlus?.classList.remove('hidden')

    let iconReProcess = document.getElementById(
      're-process-button-' + props.index
    )
    iconReProcess?.classList.remove('hidden')
  }
})

/* Datos componente table */
const showTable = ref(false)
const columns = ref([
  { key: 'conceptName', title: t('fields.conceptId') },
  { key: 'dataQuantity', title: t('fields.quantity') },
  { key: 'dataAmount', title: t('fields.amount') },
  { key: 'averages', title: '' }
])
const columnsAverages = ref([
  { key: 'start', title: t('fields.startDate') },
  { key: 'end', title: t('fields.endDate') },
  { key: 'dataQuantity', title: t('fields.quantity') },
  { key: 'dataAmount', title: t('fields.amount') },
  { key: 'appliedA', title: t('liquidations.components.apply') }
])

/* Datos componente Modal y Loading */
const showModal = ref(false)
const showModalAverages = ref(false)
const dataAverages = ref(<any>[])
const averagesNew = ref(<any>[])
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const saveEmployeeId = ref()
const actionRemove = ref()

/* filtrar informacion del empleado */
const dataEmployees = (id: string, key: string) => {
  return props.liquidation.employee_info.filter(
    (item: any) => item.id === id
  )[0][key]
}

/* Mostrar/ocultar desplegable */
const dropdownEmployees = (e: any) => {
  showTable.value = false
  const parents = e.currentTarget.parentNode.parentNode.parentNode.parentNode
  const parentItem =
    parents.parentNode.parentNode.getElementsByClassName('drop_container')
  const iconEye = parents.getElementsByClassName('eyeIcon')
  const iconEyeOff = parents.getElementsByClassName('eyeOffIcon')

  let iconPlus
  let iconReProcess

  if (props.liquidation.status_code !== 'COMPLETE') {
    iconPlus = parents.getElementsByClassName('PlusIcon')
    iconReProcess = parents.getElementsByClassName('reProcess')
  }

  if (parentItem[0].classList.contains('hide')) {
    parentItem[0].classList.remove('hide')
    parentItem[0].classList.add('show')
    iconEyeOff[0].classList.remove('hidden')
    iconEye[0].classList.add('hidden')
  } else {
    parentItem[0].classList.remove('show')
    parentItem[0].classList.add('hide')
    iconEye[0].classList.remove('hidden')
    iconEyeOff[0].classList.add('hidden')
    if (props.liquidation.status_code !== 'COMPLETE') {
      iconPlus[0].classList.add('hidden')
      iconReProcess[0].classList.add('hidden')
    }
  }
}

/* Actualizar informacion de un empleado */
const emitUpdateEmployee = (
  idEmployee: string,
  concept: any = '',
  average: any = []
) => {
  const data: AdjustsData = {
    isRecontractable: isRecontractable.value ?? props.employee.isRecontractable,
    observation: observation.value ?? props.employee.observation,
    conceptsToAdd: concepts.value,
    adjusts: []
  }
  if (concept !== '') {
    data.adjusts.push({
      conceptId: concept.id,
      quantity:
        concept.text !== 'quantity'
          ? parseFloat(concept.value).toString()
          : adjustQuantity.value === ''
          ? '0'
          : parseFloat(adjustQuantity.value).toString(),
      amount:
        concept.text !== 'amount'
          ? parseFloat(concept.value).toString()
          : adjustAmount.value === ''
          ? '0'
          : parseFloat(adjustAmount.value).toString(),
      adjustAverage: concept.adjustAverage
    })
  } else if (Object.entries(average).length > 0) {
    data.adjusts.push({
      conceptId: average.conceptId,
      quantity: average.quantity,
      amount: average.amount,
      adjustAverage: averagesNew.value
    })
    closeModal()
  }
  concepts.value = []
  emit('update:employee', { idEmployee, data, index: props.index })
  showTable.value = false
}

/* Movimientos del empleados */
const emitMovementsEmployee = (employeeId: string) => {
  let iconReProcess = document.getElementById(
    're-process-button-' + props.index
  )
  emit('get:movements', employeeId, props.index, iconReProcess)
}

/* Eliminar empleado */
const removeEmployee = (employeeId: string) => {
  const employeeName: string = dataEmployees(employeeId, 'name_employee')
  showModal.value = true
  titleModal.value = t('liquidations.components.deleteEmployee', {
    employeeName: employeeName
  })
  descriptionModal.value = t('modal.description')
  saveEmployeeId.value = employeeId
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  actionRemove.value = emitRemoveEmployee
}
const emitRemoveEmployee = () => {
  showModal.value = false
  emit('remove:employee', saveEmployeeId.value)
  hideDropdown()
}

/* Reprocesar un empleado */
const reProcessEmployee = (employeeId: string) => {
  const employeeName: string = dataEmployees(employeeId, 'name_employee')
  showModal.value = true
  titleModal.value = t('liquidations.components.reprocessEmployee', {
    employeeName: employeeName
  })
  descriptionModal.value = t('modal.description')
  saveEmployeeId.value = employeeId
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('liquidations.components.reprocess')
  })
  actionRemove.value = emitReProcessEmployee
}
const emitReProcessEmployee = () => {
  showModal.value = false
  hideDropdown()
  let eyeButton = document.getElementById('eye-icon-button-' + props.index)
  eyeButton?.classList.add('hidden')

  let trashButton = document.getElementById('trash-icon-button-' + props.index)
  trashButton?.classList.add('hidden')

  let printButton = document.getElementById('print-icon-button-' + props.index)
  printButton?.classList.add('hidden')

  let loadingIcon = document.getElementById(
    'loading-icon-' + props.index
  )
  loadingIcon?.classList.remove('hidden')

  let reprocessButton = document.getElementById(
    're-process-button-' + props.index
  )
  emit(
    'reprocess:employee',
    saveEmployeeId.value,
    eyeButton,
    reprocessButton,
    trashButton,
    printButton,
    loadingIcon
  )
}

/* ocultar dropdown del usuario */
const hideDropdown = () => {
  const eyeOffIconElement =
    document.getElementsByClassName('eyeOffIcon')[props.index]
  const ocultar = document.getElementById(`hideDropdown${props.index}`)
  if (Array.from(eyeOffIconElement.classList).length === 1) ocultar?.click()
}

/* Agregar un value al input */
const addValueInput = (value: string, text: string) => {
  if (parseFloat(value) === 0) value = ''
  text === 'quantity'
    ? (adjustQuantity.value = value)
    : (adjustAmount.value = value)
}

/* Validacion de numeros para cantidad y monto */
const sanitizeNumberInput = (input: any, positive = true) => {
  if (!input) return ''
  const sanitizedValue = input
    .replace(/[^\d.-]|(?!^)-/g, '')
    .replace(/^-{2,}/, '-')
    .replace(/^0(?![.0-9])/, '')
    .replace(/(\d*\.\d*)\./, '$1')
    .slice(0, 12)
  return positive ? sanitizedValue : sanitizedValue.replace(/[^0-9.]/g, '')
}

const validNumber = (positive = true) => {
  adjustAmount.value = sanitizeNumberInput(adjustAmount.value, positive)
  adjustQuantity.value = sanitizeNumberInput(adjustQuantity.value, positive)
}

const changeAverage = (value: any, data: any) => {
  let average = averagesNew.value.filter((item: any) => {
    const itemStartDate = item.start
    const itemEndDate = item.end
    return itemStartDate <= data.end && itemEndDate >= data.start
  })
  if (value === 'quantity') {
    average[0].quantity = Number(adjustQuantity.value)
  } else {
    average[0].amount = Number(adjustAmount.value)
  }
}

const changeApplied = (data: any, value: any) => {
  let average = averagesNew.value.filter((item: any) => {
    const itemStartDate = item.start
    const itemEndDate = item.end
    return itemStartDate <= data.end && itemEndDate >= data.start
  })
  average[0].applied = value
}

/* abrir modal de promedios */
const openAverages = (data: any, average: any) => {
  if (average.adjustAverage.length === 0) {
    averagesNew.value = average.average.historic.rows
  } else {
    averagesNew.value = JSON.parse(JSON.stringify(average.adjustAverage))
  }
  dataAverages.value = data
  showModalAverages.value = true
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModalAverages.value = false
  showModal.value = false
  averagesNew.value = []
}

const generateLiquidationReport = (employeeId: string) => {
  const { reportLiquidationEmployee } = props.liquidation
  const report = reportLiquidationEmployee
  report.parameters.employees = []
  report.parameters.employees.push(employeeId)
  emit('liquidationReport:employee', report)
}

const emitCheckLiquidationStatus = () => {
  let eyeButton = document.getElementById('eye-icon-button-' + props.index)
  let trashIcon = document.getElementById('trash-icon-button-' + props.index)
  let printIcon = document.getElementById('print-icon-button-' + props.index)

  let loadingIcon = document.getElementById(
    'loading-icon-' + props.index
  )

  let reprocessButton = document.getElementById(
    're-process-button-' + props.index
  )
  emit('check:liquidation', eyeButton, reprocessButton, trashIcon, printIcon, loadingIcon)
}

onMounted(() => {
  emitCheckLiquidationStatus()
  observation.value = props.employee.observation
  isRecontractable.value = props.employee.isRecontractable
  additionalConcepts.value = props.itemsAdditionalConcepts.filter(
    item =>
      !props.employee.concepts.some(
        (item2: { id: any }) => item2.id === item.id
      )
  )
})
</script>
<template>
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="actionRemove" />
  <DModal
    :open="showModalAverages"
    size="md:w-full lg:w-full xl:w-4/6"
    title=""
    @closeModal="closeModal">
    <template #form>
      <div class="text-left w-full px-2" style="margin-top: -10px">
        <div class="mb-3">
          <p class="text-xl">
            <span class="font-semibold">{{
              t('liquidations.components.averageAdjustment')
            }}</span>
            {{ dataAverages.conceptName }}
          </p>
          <p class="text-xl mb-3">
            <span class="font-semibold">{{
              t('liquidations.components.average')
            }}</span>
            {{ formatDynamicNumber(Number(dataAverages.amount)) }}
          </p>
          <p class="text-sm bg-blue-100 w-max">
            {{ t('liquidations.components.periodsNotConsidered') }}
          </p>
        </div>
        <div>
          <TableLiquidation
            :columns="columnsAverages"
            :rows="averagesNew"
            height-table="500px"
            classTable="tableAverage">
            <template #dataQuantity="average: any">
              <div class="flex items-center gap-1">
                <p class="font-semibold">
                  {{ formatDynamicNumber(Number(average.quantity)) }}
                </p>
                <div
                  class="px-1.5 py-0.5 rounded-full text-sm flex items-center gap-2">
                  <Popper
                    v-if="
                      liquidation.status_code === 'PENDING' ||
                      liquidation.status_code === 'ERROR'
                        ? true
                        : false
                    "
                    arrow
                    offsetDistance="14"
                    :hover="false"
                    placement="bottom"
                    class="popper-input"
                    @open:popper="addValueInput(average.quantity, 'quantity')">
                    <div class="cursor-pointer">
                      <Edit01Icon
                        class="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </div>
                    <template #content="{ close }">
                      <div class="flex justify-center items-center gap-2">
                        <DTextField
                          v-model="adjustQuantity"
                          class="w-32"
                          classInput="py-1 px-1.5 focus:ring-gray-300 "
                          @input="validNumber(false)" />
                        <div @click="close">
                          <CheckIcon
                            @click="changeAverage('quantity', average)"
                            class="h-7 w-7 cursor-pointer p-1 rounded-full bg-green-100 text-green-500 hover:bg-green-200" />
                        </div>
                      </div>
                    </template>
                  </Popper>
                </div>
              </div>
            </template>
            <template #dataAmount="average: any">
              <div class="flex items-center gap-1">
                <p class="font-semibold">
                  {{ formatDynamicNumber(Number(average.amount)) }}
                </p>
                <div
                  class="px-1.5 py-0.5 rounded-full text-sm flex items-center gap-2">
                  <Popper
                    v-if="
                      liquidation.status_code === 'PENDING' ||
                      liquidation.status_code === 'ERROR'
                        ? true
                        : false
                    "
                    arrow
                    offsetDistance="14"
                    :hover="false"
                    placement="bottom"
                    class="popper-input"
                    @open:popper="addValueInput(average.amount, 'amount')">
                    <div class="cursor-pointer">
                      <Edit01Icon
                        class="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </div>
                    <template #content="{ close }">
                      <div class="flex justify-center items-center gap-2">
                        <DTextField
                          v-model="adjustAmount"
                          class="w-32"
                          classInput="py-1 px-1.5 focus:ring-gray-300 "
                          @input="validNumber(false)" />
                        <div @click="close">
                          <CheckIcon
                            @click="changeAverage('amount', average)"
                            class="h-7 w-7 cursor-pointer p-1 rounded-full bg-green-100 text-green-500 hover:bg-green-200" />
                        </div>
                      </div>
                    </template>
                  </Popper>
                </div>
              </div>
            </template>
            <template #appliedA="average: any">
              <DToggleField
                v-model="average.applied"
                label=""
                color="green"
                @change="changeApplied(average, average.applied)" />
            </template>
          </TableLiquidation>
        </div>
      </div>
    </template>
    <template #actions>
      <footer
        class="sticky inset-x-0 bottom-0 bg-gray-100 w-full flex items-center pt-4 pb-4 pr-2 h-17 justify-end">
        <div>
          <DBtn
            type="button"
            color="secondary"
            class="py-2 px-8 mx-2"
            @click="closeModal">
            {{ t('form.button.close') }}
          </DBtn>
        </div>
        <div>
          <DBtn
            class="py-2 px-8 mx-2"
            @click="
              emitUpdateEmployee(employee.id, '', {
                conceptId: dataAverages.conceptId,
                amount: dataAverages.adjustAmount,
                quantity: dataAverages.adjustQuantity
              })
            ">
            {{ t('form.button.save') }}
          </DBtn>
        </div>
      </footer>
    </template>
  </DModal>
  <AdditionalConcepts
    v-model="concepts"
    :options="additionalConcepts"
    :openMultiSelect="openAdditionalConcepts"
    @change:close="openAdditionalConcepts = false"
    @change:add="
      ;(openAdditionalConcepts = false), emitUpdateEmployee(employee.id)
    " />
  <div
    class="py-2 px-3 mx-3 mb-2 shadow-md rounded-xl border-t border-x border-x-gray-100 border-t-gray-100 bg-white">
    <div class="grid grid-cols-7 gap-4">
      <div class="col-span-4 flex items-center">
        <p
          class="font-semibold text-gray-700 leading-5 whitespace-nowrap text-ellipsis overflow-hidden max-w-lg">
          {{ dataEmployees(employee.id, 'name') }} <br />
          <span class="font-normal text-gray-500">
            {{ dataEmployees(employee.id, 'identification_number') }}
          </span>
        </p>
      </div>
      <div class="col-span-2 flex items-center">
        <p class="font-semibold text-gray-600 leading-5">
          {{
            dayjs
              .utc(dataEmployees(employee.id, 'joining_date'))
              .format('DD/MM/YYYY')
          }}
          -
          {{
            employee.leaveDate === null
              ? dayjs.utc(liquidation.leave_date).format('DD/MM/YYYY')
              : dayjs.utc(employee.leaveDate).format('DD/MM/YYYY')
          }}
        </p>
      </div>
      <div class="col-span-1 flex justify-end items-center gap-4">
        <div class="eyeIcon hidden" :id="'eye-icon-button-' + index">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('form.button.watch')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
              @click="
                dropdownEmployees($event), emitMovementsEmployee(employee.id)
              ">
              <EyeIcon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div class="eyeOffIcon hidden">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('form.button.disguise')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
              @click="dropdownEmployees($event)"
              :id="'hideDropdown' + index">
              <EyeOffIcon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div
          v-if="
            liquidation.status_code === 'PENDING' ||
            liquidation.status_code === 'ERROR'
              ? true
              : false
          "
          :id="'plus-button-' + index"
          class="PlusIcon hidden">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('liquidations.components.addAdditionalConcepts')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-green-100 text-green-600"
              @click="openAdditionalConcepts = true">
              <PlusIcon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div
          v-if="
            liquidation.status_code === 'PENDING' ||
            liquidation.status_code === 'ERROR'
              ? true
              : false
          "
          :id="'re-process-button-' + index"
          class="reProcess hidden">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('liquidations.components.reprocessEmployeeLabel')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-blue-100 text-blue-600"
              @click="reProcessEmployee(employee.id)">
              <CalculatorIcon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div :id="'print-icon-button-' + index" class="printIcon hidden">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('table.action.print')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
              @click="generateLiquidationReport(employee.id)">
              <PrinterIcon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div
          :id="'trash-icon-button-' + index"
          class="trashIcon hidden">
          <Popper
            arrow
            offsetDistance="13"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="p-2 rounded-md cursor-pointer bg-red-100 text-red-600"
              @click="removeEmployee(employee.id)">
              <Trash03Icon style="width: 22px; height: 22px" />
            </div>
          </Popper>
        </div>
        <div :id="'loading-icon-' + index" class="loading">
          <Loading02Icon class="rotate h-7 w-7 text-blue-700" />
        </div>
      </div>
    </div>
    <div class="drop_container hide">
      <div
        v-if="!showTable"
        class="flex justify-center items-center gap-2 h-80">
        <Loading02Icon class="rotate h-7 w-7 text-blue-700" />
        <p class="text-blue-700 font-semibold">
          {{
            liquidation.status_code === 'PENDING' ||
            liquidation.status_code === 'ERROR'
              ? t('liquidations.components.updating') + '...'
              : t('liquidations.components.loading') + '...'
          }}
        </p>
      </div>
      <div v-else class="flex gap-4">
        <div class="pt-3 px-0.5 pb-1 w-96">
          <DToggleField
            v-model="isRecontractable"
            :label="t('liquidations.components.rehireable')"
            @change="emitUpdateEmployee(employee.id)"
            :disabled="
              liquidation.status_code === 'PENDING' ||
              liquidation.status_code === 'ERROR'
                ? false
                : true
            " />
          <DTextAreaField
            v-model="observation"
            @change="emitUpdateEmployee(employee.id)"
            class="pt-3 pb-1.5"
            :label="t('liquidations.components.observations')"
            :resize="false"
            :rows="5"
            :disabled="
              liquidation.status_code === 'PENDING' ||
              liquidation.status_code === 'ERROR'
                ? false
                : true
            " />
        </div>
        <div class="pt-3 w-full">
          <div class="pb-2">
            <TableLiquidation
              :columns="columns"
              :rows="conceptsEmployee"
              height-table="400px"
              classTable="tableLiquidation">
              <template #dataQuantity="concept: any">
                <div class="flex items-center gap-1">
                  <p class="font-semibold">
                    {{ formatDynamicNumber(Number(concept.quantity)) }}
                  </p>
                  <div
                    class="px-1.5 py-0.5 rounded-full text-sm flex items-center gap-2"
                    :class="
                      Number(concept.adjustQuantity) === 0
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-orange-100 text-orange-600'
                    ">
                    <p class="font-semibold">
                      {{
                        `${
                          concept.adjustQuantity > 0 ? '+' : ''
                        }${formatDynamicNumber(Number(concept.adjustQuantity))}`
                      }}
                    </p>
                    <Popper
                      v-if="
                        liquidation.status_code === 'PENDING' ||
                        liquidation.status_code === 'ERROR'
                          ? true
                          : false
                      "
                      arrow
                      offsetDistance="14"
                      :hover="false"
                      placement="bottom"
                      class="popper-input"
                      @open:popper="
                        addValueInput(concept.adjustQuantity, 'quantity')
                      ">
                      <div class="cursor-pointer">
                        <Edit01Icon
                          class="h-4 w-4 text-gray-500 hover:text-gray-700" />
                      </div>
                      <template #content="{ close }">
                        <div class="flex justify-center items-center gap-2">
                          <DTextField
                            v-model="adjustQuantity"
                            class="w-32"
                            classInput="py-1 px-1.5 focus:ring-gray-300 "
                            @input="validNumber" />
                          <div @click="close">
                            <CheckIcon
                              @click="
                                emitUpdateEmployee(employee.id, {
                                  id: concept.conceptId,
                                  value: concept.adjustAmount,
                                  text: 'quantity',
                                  adjustAverage: concept.adjustAverage
                                })
                              "
                              class="h-7 w-7 cursor-pointer p-1 rounded-full bg-green-100 text-green-500 hover:bg-green-200" />
                          </div>
                        </div>
                      </template>
                    </Popper>
                  </div>
                </div>
              </template>
              <template #dataAmount="concept: any">
                <div class="flex items-center gap-1">
                  <p class="font-semibold">
                    {{ formatDynamicNumber(Number(concept.amount)) }}
                  </p>
                  <div
                    class="px-1.5 py-0.5 rounded-full text-sm flex items-center gap-2"
                    :class="
                      Number(concept.adjustAmount) === 0
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-orange-100 text-orange-600'
                    ">
                    <p class="font-semibold">
                      {{
                        `${
                          concept.adjustAmount > 0 ? '+' : ''
                        }${formatDynamicNumber(Number(concept.adjustAmount))}`
                      }}
                    </p>
                    <Popper
                      v-if="
                        liquidation.status_code === 'PENDING' ||
                        liquidation.status_code === 'ERROR'
                          ? true
                          : false
                      "
                      arrow
                      offsetDistance="14"
                      :hover="false"
                      placement="bottom"
                      class="popper-input"
                      @open:popper="
                        addValueInput(concept.adjustAmount, 'amount')
                      ">
                      <div class="cursor-pointer">
                        <Edit01Icon
                          class="h-4 w-4 text-gray-500 hover:text-gray-700" />
                      </div>
                      <template #content="{ close }">
                        <div class="flex justify-center items-center gap-2">
                          <DTextField
                            v-model="adjustAmount"
                            class="w-32"
                            classInput="py-1 px-1.5 focus:ring-gray-300 "
                            @input="validNumber" />
                          <div @click="close">
                            <CheckIcon
                              @click="
                                emitUpdateEmployee(employee.id, {
                                  id: concept.conceptId,
                                  value: concept.adjustQuantity,
                                  text: 'amount',
                                  adjustAverage: concept.adjustAverage
                                })
                              "
                              class="h-7 w-7 cursor-pointer p-1 rounded-full bg-green-100 text-green-500 hover:bg-green-200" />
                          </div>
                        </div>
                      </template>
                    </Popper>
                  </div>
                </div>
              </template>
              <template #averages="concept: any">
                <Popper
                  v-if="concept.average !== null"
                  arrow
                  offsetDistance="13"
                  :content="t('liquidations.components.averages')"
                  :hover="true"
                  placement="top"
                  class="tooltip">
                  <div
                    class="p-2 rounded-md cursor-pointer bg-blue-100 text-blue-600"
                    style="margin: 3px 0 -4px"
                    @click="
                      openAverages(concept, {
                        adjustAverage: concept.adjustAverage,
                        average: concept.average
                      })
                    ">
                    <BarChart10Icon style="width: 22px; height: 22px" />
                  </div>
                </Popper>
              </template>
            </TableLiquidation>
          </div>
          <div class="pr-6 flex justify-end py-4">
            <p class="font-bold text-gray-700">
              {{ t('liquidations.components.netToPay') }} &nbsp;&nbsp;&nbsp;
            </p>
            <p class="font-semibold text-blue-700">
              ${{ formatDynamicNumber(Number(payNeto)) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.drop_container {
  overflow: hidden;
  max-height: 0;
}
.drop_container.show {
  max-height: 550px;
  transition: max-height 0.5s ease-in;
}
.drop_container.hide {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.5s ease-out;
}
.popper-input {
  --popper-theme-background-color: #ffffff;
  --popper-theme-background-color-hover: #ffffff;
  --popper-theme-text-color: #666f85;
  --popper-theme-border-radius: 10px;
  --popper-theme-padding: 8px 10px;
  --popper-theme-box-shadow: -1px 2px 13px -4px rgba(0, 0, 0, 0.25);
  font-size: 14px;
}
</style>
